import { Controller } from "@hotwired/stimulus"

/**
 * Section reordering and visibility for CV Builder.
 * Handles drag-and-drop reordering of CV sections and show/hide toggles.
 * Dispatches "input" events to trigger the parent cv-builder auto-save.
 */
export default class extends Controller {
  static targets = ["section"]

  connect() {
    this._dragging = null
  }

  disconnect() {
    this._dragging = null
  }

  // --- Drag handle: enable draggable only from the handle ---

  handleDown(e) {
    const section = e.target.closest("[data-section-name]")
    if (section && section.dataset.sectionActive === "true") {
      section.draggable = true
    }
  }

  handleUp(e) {
    const section = e.target.closest("[data-section-name]")
    if (section) section.draggable = false
  }

  // --- HTML5 Drag and Drop ---

  dragStart(e) {
    const section = e.target.closest("[data-section-name]")
    if (!section || section.dataset.sectionActive !== "true") {
      e.preventDefault()
      return
    }
    this._dragging = section
    section.classList.add("opacity-50")
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", section.dataset.sectionName)
  }

  dragOver(e) {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"

    const target = e.target.closest("[data-section-name]")
    if (!target || target === this._dragging) return
    if (target.dataset.sectionActive !== "true") return

    const rect = target.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    const insertBefore = e.clientY < midY ? target : target.nextSibling

    if (insertBefore !== this._dragging && insertBefore !== this._dragging.nextSibling) {
      target.parentNode.insertBefore(this._dragging, insertBefore)
    }
  }

  dragEnd(e) {
    if (this._dragging) {
      this._dragging.classList.remove("opacity-50")
      this._dragging.draggable = false
      this._dragging = null
    }
    this._dispatchChange()
  }

  // --- Show/hide sections ---

  showSection(e) {
    const wrapper = e.target.closest("[data-section-name]")
    if (!wrapper) return

    wrapper.dataset.sectionActive = "true"
    const inactive = wrapper.querySelector("[data-role='inactive']")
    const active = wrapper.querySelector("[data-role='active']")
    if (inactive) inactive.classList.add("hidden")
    if (active) active.classList.remove("hidden")

    // Move to end of active sections (before first inactive)
    const firstInactive = this.sectionTargets.find(
      (s) => s.dataset.sectionActive === "false" && s !== wrapper
    )
    if (firstInactive) {
      wrapper.parentNode.insertBefore(wrapper, firstInactive)
    }

    this._dispatchChange()
  }

  hideSection(e) {
    e.stopPropagation()
    const wrapper = e.target.closest("[data-section-name]")
    if (!wrapper) return

    wrapper.dataset.sectionActive = "false"
    wrapper.draggable = false
    const inactive = wrapper.querySelector("[data-role='inactive']")
    const active = wrapper.querySelector("[data-role='active']")
    if (active) active.classList.add("hidden")
    if (inactive) inactive.classList.remove("hidden")

    // Move to end (after all active sections)
    wrapper.parentNode.appendChild(wrapper)

    this._dispatchChange()
  }

  // --- Helpers ---

  getSectionOrder() {
    return this.sectionTargets
      .filter((s) => s.dataset.sectionActive === "true")
      .map((s) => s.dataset.sectionName)
  }

  _dispatchChange() {
    this.element.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
